import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import UnifiedDiffView from './UnifiedDiffView';
import {describeToolCall, toolCallStatus} from './WorkflowTimeline';
import * as vfsClient from '../utils/vfsClient';
import './ToolCallDetailSheet.css';

// A 'write' tool call's commit sha — vfs_write_file exposes it as
// `commitSha` (added specifically for this feature), vfs_patch as
// `newVersion` (already existed, diffApply.js's own return shape).
function commitShaOf(toolCall) {
    return toolCall.response?.commitSha || toolCall.response?.newVersion || null;
}

// vfs_patch's own call arguments already carry the complete, ready-to-render
// unified diff the agent authored (vfs-service's vfs_patch tool takes
// {path, diff} and applies it directly — no server round-trip needed to see
// it again, exactly like the vendored chat widget's inline raw view already
// renders it via fileEditPreview.ts). Using this directly instead of always
// fetching via commitShaOf/getCommitDiff means the modal shows a real diff
// even when the commit-sha lookup fails/races/isn't available yet — which is
// what was actually happening: a successful vfs_patch call with a perfectly
// good newVersion sha, but the corresponding /history/:sha/diff round-trip
// wasn't the only path that should exist for data the call already has.
//
// vfs_patch's diff argument is HEADERLESS (just `@@ ... @@` hunks + +/-/
// context lines, no `diff --git`/`---`/`+++` lines) — UnifiedDiffView's
// parser expects those header lines to know which file a hunk belongs to,
// so wrap it in a minimal synthetic header using the call's own path.
function directDiffOf(toolCall) {
    if (toolCall.name !== 'vfs_patch' || typeof toolCall.args?.diff !== 'string' || !toolCall.args.diff.trim()) {
        return null;
    }
    const path = toolCall.args?.path || 'file';
    return `diff --git a/${path} b/${path}\n--- a/${path}\n+++ b/${path}\n${toolCall.args.diff}`;
}

// Same failure shape WorkflowTimeline.jsx's own toolCallStatus checks (and
// the vendored widget's extractToolError mirrors independently) — a write
// with no commit sha is either a failed call (this) or a successful one that
// simply predates commitSha being added to the response; without this check
// both looked identical ("No diff available for this step."), hiding the
// actual MCP error text the inline chat view already showed for the same
// tool call.
function errorMessageOf(toolCall) {
    const response = toolCall.response;
    if (!response || typeof response !== 'object') {
        return null;
    }
    const failed = response.success === false || response.isError === true || response.ok === false ||
        (typeof response.error === 'string' && response.error.trim() !== '');
    if (!failed) {
        return null;
    }
    return typeof response.error === 'string' && response.error.trim()
        ? response.error.trim()
        : (response.message || 'This step failed.');
}

/**
 * Detail sheet for one WorkflowTimeline group: lists every tool call the
 * agent made in that turn, and lets the user drill into any file edit (a
 * real, line-numbered unified diff, fetched lazily via vfsClient.
 * getCommitDiff — no diff data is ever fetched just to render the
 * collapsed summary) or command (its full stdout/stderr, already present
 * in the tool call's own response, no fetch needed).
 */
export default function ToolCallDetailSheet({group, vfsProjectId, onClose}) {
    const [drillId, setDrillId] = useState(null);
    const [diffText, setDiffText] = useState(null);
    const [diffLoading, setDiffLoading] = useState(false);
    const [diffError, setDiffError] = useState(null);

    const drillToolCall = drillId ? group.toolCalls.find((tc) => tc.id === drillId) : null;
    const drillKind = drillToolCall ? describeToolCall(drillToolCall).kind : null;

    useEffect(() => {
        if (!drillToolCall || drillKind !== 'write' || directDiffOf(drillToolCall)) {
            return;
        }
        const sha = commitShaOf(drillToolCall);
        if (!vfsProjectId || !sha) {
            setDiffText(null);
            setDiffError(null);
            return;
        }
        let cancelled = false;
        setDiffLoading(true);
        setDiffError(null);
        vfsClient.getCommitDiff(vfsProjectId, sha)
            .then((text) => { if (!cancelled) { setDiffText(text); } })
            .catch((err) => { if (!cancelled) { setDiffError(err.message || 'Failed to load diff'); } })
            .finally(() => { if (!cancelled) { setDiffLoading(false); } });
        return () => { cancelled = true; };
    }, [drillToolCall, drillKind, vfsProjectId]);

    const title = drillToolCall ? describeToolCall(drillToolCall).verb : 'Workflow steps';

    return (
        <Modal
            className="tool-call-detail-modal"
            isOpen
            onClose={onClose}
            showCancel={false}
            title={title}
            type="custom"
        >
            <div className="tool-call-detail-body">
                {drillToolCall ? (
                    <>
                        <button className="tool-call-detail-back" onClick={() => setDrillId(null)} type="button">
                            ‹ Back to steps
                        </button>
                        {drillKind === 'write' && (() => {
                            const directDiff = directDiffOf(drillToolCall);
                            if (directDiff) {
                                return (
                                    <>
                                        <div className="tool-call-detail-file">{drillToolCall.args?.path}</div>
                                        <UnifiedDiffView diffText={directDiff} />
                                    </>
                                );
                            }
                            return (
                                <>
                                    <div className="tool-call-detail-file">{drillToolCall.args?.path}</div>
                                    {diffLoading && <div className="tool-call-detail-status">Loading diff…</div>}
                                    {diffError && <div className="tool-call-detail-status tool-call-detail-error">{diffError}</div>}
                                    {!diffLoading && !diffError && diffText && <UnifiedDiffView diffText={diffText} />}
                                    {!diffLoading && !diffError && !diffText && (() => {
                                        const errorMessage = errorMessageOf(drillToolCall);
                                        if (errorMessage) {
                                            return <pre className="tool-call-detail-output tool-call-detail-error">{errorMessage}</pre>;
                                        }
                                        if (typeof drillToolCall.args?.content === 'string') {
                                            // ADK 2.10 streams a vfs_write_file call's args in
                                            // (see mia-chatbot-widget's ADKPartialArg) — while
                                            // still running, `content` grows turn by turn with
                                            // no commit yet (there's nothing to commit until
                                            // the call actually completes), which used to read
                                            // as the SAME "no commit found" message a genuinely
                                            // failed/pre-commitSha call gets. `toolCallStatus`
                                            // already reads 'running' for exactly this case
                                            // (no endedAt yet) — say so honestly instead.
                                            const stillWriting = toolCallStatus(drillToolCall) === 'running';
                                            return (
                                                <>
                                                    <div className="tool-call-detail-status">
                                                        {stillWriting
                                                            ? 'Still writing — showing content as it arrives.'
                                                            : 'No commit found for this step — showing the attempted content.'}
                                                    </div>
                                                    <pre className="tool-call-detail-output">{drillToolCall.args.content}</pre>
                                                </>
                                            );
                                        }
                                        return <div className="tool-call-detail-status">No diff available for this step.</div>;
                                    })()}
                                </>
                            );
                        })()}
                        {drillKind === 'command' && (
                            <>
                                <pre className="tool-call-detail-command">{drillToolCall.args?.command}</pre>
                                {drillToolCall.response?.stdout && (
                                    <>
                                        <div className="tool-call-detail-subhead">stdout</div>
                                        <pre className="tool-call-detail-output">{drillToolCall.response.stdout}</pre>
                                    </>
                                )}
                                {drillToolCall.response?.stderr && (
                                    <>
                                        <div className="tool-call-detail-subhead">stderr</div>
                                        <pre className="tool-call-detail-output tool-call-detail-error">{drillToolCall.response.stderr}</pre>
                                    </>
                                )}
                                {typeof drillToolCall.response?.exitCode === 'number' && (
                                    <div className="tool-call-detail-subhead">
                                        Exit code: {drillToolCall.response.exitCode}
                                    </div>
                                )}
                            </>
                        )}
                        {drillKind !== 'write' && drillKind !== 'command' && (
                            <pre className="tool-call-detail-output">
                                {JSON.stringify({args: drillToolCall.args, response: drillToolCall.response}, null, 2)}
                            </pre>
                        )}
                    </>
                ) : (
                    <ul className="tool-call-detail-list">
                        {group.toolCalls.map((toolCall) => {
                            const {verb, kind, summary} = describeToolCall(toolCall);
                            const status = toolCallStatus(toolCall);
                            const drillable = kind === 'write' || kind === 'command';
                            return (
                                <li key={toolCall.id}>
                                    <button
                                        className={`tool-call-detail-item is-${status}`}
                                        disabled={!drillable}
                                        onClick={() => setDrillId(toolCall.id)}
                                        type="button"
                                    >
                                        <span className="tool-call-detail-item-verb">{verb}</span>
                                        {summary && <span className="tool-call-detail-item-summary">{summary}</span>}
                                        {drillable && <span className="tool-call-detail-item-chevron">›</span>}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </Modal>
    );
}

ToolCallDetailSheet.propTypes = {
    group: PropTypes.shape({
        turnIndex: PropTypes.number,
        toolCalls: PropTypes.array.isRequired,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    vfsProjectId: PropTypes.string,
};
